import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, ListSubheader, MenuItem, Stack, TextField } from '@mui/material'

import { InternalBrand } from '../../../brands/types/InternalBrand'
import { getSubgenreGroups } from '../../../games/helpers/editGameHelpers'
import { InternalGameDocument } from '../../../games/types/InternalGame'

type AnalysisGameEditorsProps = {
  game: InternalGameDocument
  subgenres: { name: string; parentGenre?: string }[]
  subgenresFailed?: boolean
  brands: InternalBrand[]
  brandsFailed?: boolean
  // The brand picked on this page: no game read returns one.
  selectedBrandId: string
  disabled?: boolean
  onSubgenreChange: (name: string) => void
  onOverrideChange: (override: boolean) => void
  onBrandChange: (brandId: string) => void
}

const byName = (a: InternalBrand, b: InternalBrand) => (a.names?.en ?? '').localeCompare(b.names?.en ?? '')

export const AnalysisGameEditors: FC<AnalysisGameEditorsProps> = ({
  game,
  subgenres,
  subgenresFailed,
  brands,
  brandsFailed,
  selectedBrandId,
  disabled,
  onSubgenreChange,
  onOverrideChange,
  onBrandChange,
}) => {
  const { t } = useTranslation()
  const storedSubgenre = game.conventionalSubgenre ?? ''
  const subgenreGroups = useMemo(
    () => getSubgenreGroups(subgenres, storedSubgenre, t('internal-analysts:games_subgenre_not_listed')),
    [storedSubgenre, subgenres, t]
  )
  const sortedBrands = useMemo(() => [...brands].sort(byName), [brands])

  const subgenreItems: ReactNode[] = subgenreGroups.flatMap(({ genre, names }) => [
    <ListSubheader key={`genre-${genre}`}>{genre}</ListSubheader>,
    ...names.map((name) => (
      <MenuItem key={`${genre}-${name}`} value={name}>
        {name}
      </MenuItem>
    )),
  ])

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t('internal-analysts:games_subgenre')}
          value={storedSubgenre}
          disabled={disabled || !subgenres.length}
          error={subgenresFailed}
          helperText={subgenresFailed ? t('internal-analysts:games_subgenres_load_failed') : undefined}
          onChange={(event) => event.target.value !== storedSubgenre && onSubgenreChange(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {subgenreItems}
        </TextField>
        <FormControlLabel
          control={<Checkbox checked={!!game.overrideConventionals} disabled={disabled} onChange={(event) => onOverrideChange(event.target.checked)} />}
          label={t('internal-analysts:games_override_subgenre')}
        />
      </Stack>
      <TextField
        select
        size="small"
        label={t('internal-analysts:analysis_overview_brand')}
        value={selectedBrandId}
        disabled={disabled || !brands.length}
        error={brandsFailed}
        helperText={brandsFailed ? t('internal-analysts:analysis_overview_brands_failed') : t('internal-analysts:analysis_overview_brand_help')}
        onChange={(event) => event.target.value && event.target.value !== selectedBrandId && onBrandChange(event.target.value)}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ maxWidth: 360 }}
      >
        <MenuItem value="" disabled>
          {t('internal-analysts:analysis_overview_brand_none')}
        </MenuItem>
        {sortedBrands.map((brand) => (
          <MenuItem key={brand.id} value={brand.id}>
            {brand.names?.en || brand.id}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  )
}
